<template>
  <div class="public-form-container">
    <Toast :style="{ width: $isMobile() ? '85vw' : '' }" />
    <Loading :active.sync="loading" />
    <div class="content-wrapper" v-if="details">
      <div class="left-panel">
        <div class="logo-section">
          <img src="../../assets/icon_company.png" alt="Circuit Crafters Logo" class="company-logo" />
          <h2 class="company-name">Circuit Crafters</h2>
          <p class="subtitle">Assistência Técnica {{ details.equipment_brand }}</p>
        </div>

        <div class="info-section">
          <div class="p-mb-3" v-if="details.pat_v_ref">
            <h3 class="p-mb-0">V/ Referência:</h3>
            <div class="reference-number">
              <strong>{{ details.pat_v_ref }}</strong>
            </div>
          </div>
          <div class="p-mb-3" v-if="details.pat_manufacturer_ref">
            <h3 class="p-mb-0">Referência do fabricante:</h3>
            <div class="reference-number">
              <strong>{{ details.pat_manufacturer_ref }}</strong>
            </div>
          </div>
        </div>

        <div class="info-section">
          <h3>Informações de Contacto</h3>
          <div class="contact-item">
            <i class="pi pi-phone"></i>
            <span>(+351) 215 900 642</span>
          </div>
          <div class="contact-item">
            <i class="pi pi-envelope"></i>
            <span>suporte@circuitcrafters.pt</span>
          </div>
        </div>

        <div class="help-section">
          <h4>O que precisa enviar:</h4>
          <ul>
            <li>Fotos/Vídeo do problema</li>
            <li>Foto da etiqueta traseira (Modelo e Série)</li>
            <li v-if="hasWarranty">Foto da fatura/talão de compra</li>
          </ul>
        </div>
      </div>

      <div class="right-panel">
        <Card class="form-card">
          <template #title>
            <div class="form-title">
              <i class="pi pi-upload"></i>
              Envio de Documentação
            </div>
          </template>

          <template #content>
            <div v-if="!submitted">
              <Message severity="info" :closable="false" class="mb-4">
                Por favor, carregue as imagens solicitadas para darmos continuidade ao seu pedido de assistência. <br>
                <small>(*) Campos obrigatórios</small>
              </Message>

              <div class="p-fluid">

                <div class="form-section" v-if="details">
                  <h4>Dados de Contacto</h4>
                  <small>Confirme os seus dados</small>

                  <div class="p-field">
                    <label for="clientName">Nome</label>
                    <InputText
                      id="clientName"
                      v-model="formData.name"
                      placeholder="O seu nome completo"
                      disabled
                    />
                  </div>
                  <div class="p-field">
                    <label for="clientAddress">Morada *</label>
                    <InputText
                      id="clientAddress"
                      v-model="formData.address"
                      placeholder="A sua morada completa"
                      required
                    />
                  </div>
                  <div class="p-field">
                    <label for="clientZipCode">Código Postal *</label>
                    <InputText
                      id="clientZipCode"
                      v-model="formData.zipCode"
                      placeholder="O seu código postal"
                      required
                    />
                  </div>
                  <div class="p-field">
                    <label for="clientCity">Cidade *</label>
                    <InputText
                      id="clientCity"
                      v-model="formData.city"
                      placeholder="A sua cidade"
                      required
                    />
                  </div>
                </div>

                <Divider />

                <div class="form-section">
                  <h4>Documentação</h4>

                  <div class="upload-group">
                    <label class="upload-label">
                      <i class="pi pi-exclamation-circle"></i>
                      Fotos/Vídeo do Problema *
                    </label>
                    <small class="text-secondary">Mostre claramente a avaria do equipamento</small>

                    <FileUpload
                      name="problemFiles[]"
                      :multiple="true"
                      accept="image/*,video/mp4,video/x-m4v,video/quicktime,video/webm"
                      :maxFileSize="30000000"
                      @select="onProblemFilesSelect"
                      :showUploadButton="false"
                      :showCancelButton="false"
                      chooseLabel="Selecionar Ficheiros"
                    >
                      <template #empty>
                        <p>Arraste ficheiros aqui ou clique para selecionar.</p>
                        <small>Máximo 30MB por ficheiro. Formatos permitidos: jpeg, jpg, png, mp4, mov, avi, webm.</small>
                      </template>
                    </FileUpload>
                    <small v-if="files.problem.length > 0" class="p-success">
                      {{ files.problem.length }} ficheiro(s) selecionado(s)
                    </small>
                    <small v-if="submitted && files.problem.length === 0" class="p-error">
                      Pelo menos um ficheiro é obrigatório
                    </small>
                  </div>

                  <div class="upload-group">
                    <label class="upload-label">
                      <i class="pi pi-tag"></i>
                      Foto da Etiqueta Traseira *
                    </label>
                    <small class="text-secondary">Etiqueta com Modelo e Número de Série</small>

                    <FileUpload
                      name="labelFile"
                      accept="image/*"
                      :maxFileSize="10000000"
                      @select="onLabelFileSelect"
                      :showUploadButton="false"
                      :showCancelButton="false"
                      chooseLabel="Selecionar Foto"
                    >
                      <template #empty>
                        <p>Arraste a foto aqui ou clique para selecionar.</p>
                        <small>Máximo 10MB. Formatos permitidos: jpeg, jpg, png</small>
                      </template>
                    </FileUpload>
                    <small v-if="files.label" class="p-success">
                      Ficheiro selecionado: {{ files.label.name }}
                    </small>
                    <small v-if="submitted && !files.label" class="p-error">
                      Este ficheiro é obrigatório
                    </small>
                  </div>

                  <div class="warranty-switch">
                    <label class="upload-label">
                      <i class="pi pi-shield"></i>
                      O equipamento está em garantia?
                    </label>
                    <InputSwitch v-model="hasWarranty" />
                  </div>

                  <div class="upload-group" v-if="hasWarranty">
                    <label class="upload-label">
                      <i class="pi pi-file"></i>
                      Foto da Fatura/Talão de Compra *
                    </label>
                    <small class="text-secondary">Comprovativo de compra do equipamento</small>

                    <FileUpload
                      name="invoiceFile"
                      accept="image/*,application/pdf"
                      :maxFileSize="10000000"
                      @select="onInvoiceFileSelect"
                      :showUploadButton="false"
                      :showCancelButton="false"
                      chooseLabel="Selecionar Foto/PDF"
                    >
                      <template #empty>
                        <p>Arraste a foto/PDF aqui ou clique para selecionar.</p>
                        <small>Máximo 10MB. Formatos permitidos: jpeg, jpg, png, pdf.</small>
                      </template>
                    </FileUpload>
                    <small v-if="files.invoice" class="p-success">
                      Ficheiro selecionado: {{ files.invoice.name }}
                    </small>
                    <small v-if="submitted && !files.invoice" class="p-error">
                      Este ficheiro é obrigatório
                    </small>
                  </div>
                </div>

                <Divider />

                <div class="submit-section">
                  <Button
                    label="Enviar Documentação"
                    icon="pi pi-send"
                    class="p-button-lg"
                    @click="submitForm"
                    :loading="loading"
                    :disabled="loading"
                  />
                </div>
              </div>
            </div>

            <div v-else class="success-message">
              <i class="pi pi-check-circle success-icon"></i>
              <h3>Documentação Enviada com Sucesso!</h3>
              <p>Obrigado por nos enviar a documentação. Iremos analisar o seu pedido e entraremos em contacto brevemente.</p>
            </div>
          </template>
        </Card>
      </div>
    </div>

    <div v-else-if="details === null && !loading">
      <Card class="form-card">
        <template #content>
          <div class="p-text-center">
            <i class="pi pi-exclamation-triangle" style="font-size: 4rem; color: #f59e0b;"></i>
            <h3 class="p-mt-3">Serviço não encontrado ou documentação já enviada</h3>
            <p>O serviço solicitado não foi encontrado ou a documentação já foi enviada. Por favor, verifique o link ou contacte o suporte.</p>
          </div>
        </template>
      </Card>
    </div>

    <div v-else-if="loading">
      <div class="p-text-center">
        <i class="pi pi-spinner pi-spinner" style="font-size: 4rem; color: #3b82f6;"></i>
        <h3 class="p-mt-3">A carregar dados do serviço...</h3>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import PortalService from "@/services/portal.service";
import Toast from 'primevue/toast';

export default {
  name: "ServiceForm",
  components: { Loading, Toast },
  data() {
    return {
      hash: '',
      details: null,
      loading: false,
      submitted: false,
      hasWarranty: true,
      formData: {
        name: '',
        address: '',
        zipCode: '',
        city: '',
      },
      files: {
        problem: [],
        label: null,
        invoice: null
      }
    };
  },
  created() {
    if (this.$route.params.hash) {
      this.hash = this.$route.params.hash;
    }

    this.getPortalService();
  },
  methods: {
    getPortalService() {
      this.loading = true;
      PortalService.getService(this.hash).then(data => {
        this.details = data;
        if (data) {
          this.formData.name = data.client.name;
          this.formData.address = data.client.address;
          this.formData.zipCode = data.client.zipCode;
          this.formData.city = data.client.city;
        }
      }).finally(() => {
        this.loading = false;
      });
    },
    onProblemFilesSelect(event) {
      this.files.problem = event.files;
    },
    onLabelFileSelect(event) {
      this.files.label = event.files[0];
    },
    onInvoiceFileSelect(event) {
      this.files.invoice = event.files[0];
    },
    validateForm() {
      if (this.files.problem.length === 0 || !this.files.label || (this.hasWarranty && !this.files.invoice)) {
        return false;
      }

      if (!this.formData.address || !this.formData.zipCode || !this.formData.city) {
        return false;
      }

      return true;
    },
    async submitForm() {
      if (!this.validateForm()) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Campos Obrigatórios',
          detail: 'Por favor preencha todos os campos obrigatórios e anexe os ficheiros solicitados.',
          life: 5000
        });
        return;
      }

      this.loading = true;

      try {
        const formDataToSend = new FormData();

        formDataToSend.append('hash', this.hash);
        formDataToSend.append('address', this.formData.address);
        formDataToSend.append('zipCode', this.formData.zipCode);
        formDataToSend.append('city', this.formData.city);

        this.files.problem.forEach((file, index) => {
          formDataToSend.append(`problemFiles[${index}]`, file);
        });
        formDataToSend.append('labelFile', this.files.label);
        if (this.hasWarranty && this.files.invoice) {
          formDataToSend.append('invoiceFile', this.files.invoice);
        }

        const response = await PortalService.storeServiceDocuments(this.hash, formDataToSend);

        if (!response) {
          throw new Error('Erro ao enviar documentação.');
        }

        if (response.status !== 200) {
          throw new Error('Erro ao enviar documentação.');
        }

        this.submitted = true;
        this.$toast.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Documentação enviada com sucesso!',
          life: 5000
        });
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: error.message || 'Erro ao enviar documentação.',
          life: 5000
        });
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      this.submitted = false;
      this.hasWarranty = true;
      this.files = {
        problem: [],
        label: null,
        invoice: null
      };
    }
  }
};
</script>

<style>
.p-button.p-button-icon-only {
  padding: 0.75rem 1.5rem;
}
</style>

<style scoped>
.public-form-container {
  min-height: 100vh;
  background: #f5f7fb;
  padding: 2rem;
}

.content-wrapper {
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 350px 1fr;
  gap: 2rem;
  align-items: start;
}

/* Painel Esquerdo */
.left-panel {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 2rem;
}

.logo-section {
  text-align: center;
  padding-bottom: 2rem;
  border-bottom: 2px solid #f0f0f0;
}

.company-logo {
  max-width: 200px;
  height: auto;
  margin-bottom: 1rem;
}

.company-name {
  font-size: 1.5rem;
  font-weight: bold;
  color: #333;
  margin: 0.5rem 0;
}

.subtitle {
  color: #666;
  font-size: 0.9rem;
  margin: 0;
}

.info-section {
  margin-top: 2rem;
}

.info-section h3 {
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 1rem;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.contact-item i {
  color: #667eea;
  font-size: 1.1rem;
}

.help-section {
  margin-top: 2rem;
  padding: 1.5rem;
  background: #f0f7ff;
  border-radius: 8px;
  border-left: 4px solid #667eea;
}

.help-section h4 {
  color: #333;
  margin-top: 0;
  margin-bottom: 1rem;
}

.help-section ul {
  margin: 0;
  padding-left: 1.5rem;
}

.help-section li {
  margin-bottom: 0.5rem;
  color: #555;
}

/* Painel Direito */
.right-panel {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.form-card {
  box-shadow: none !important;
}

.form-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #333;
}

.form-title i {
  color: #667eea;
  font-size: 1.5rem;
}

.form-section {
  margin-bottom: 2rem;
}

.form-section h4 {
  color: #333;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #f0f0f0;
}

.upload-group {
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 2px dashed #dee2e6;
}

.upload-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
}

.upload-label i {
  color: #667eea;
}

.warranty-switch {
  margin-bottom: 1.5rem;
  padding: 1rem 1.5rem;
  background: #f0f7ff;
  border-radius: 8px;
  border-left: 4px solid #667eea;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.submit-section {
  margin-top: 2rem;
  text-align: center;
}

/* Mensagem de Sucesso */
.success-message {
  text-align: center;
  padding: 3rem 2rem;
}

.success-icon {
  font-size: 4rem;
  color: #22c55e;
  margin-bottom: 1rem;
}

.success-message h3 {
  color: #333;
  margin-bottom: 1rem;
}

.success-message p {
  color: #666;
  margin-bottom: 0.5rem;
}

.reference-number {
  font-size: 1.1rem;
  margin-top: 0.5rem;
  padding: 1rem;
  background: #f0f7ff;
  border-radius: 8px;
}

@media (max-width: 968px) {
  .content-wrapper {
    grid-template-columns: 1fr;
  }

  .left-panel {
    position: static;
  }

  .public-form-container {
    padding: 1rem;
  }
}
</style>
